import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiBaseUrl } from '@/utils/apiUtils';

interface Client {
  id: string;
  clientName: string;
}

interface EditorClientContextValue {
  currentClientId: string | null;
  currentClientName: string | null;
  clients: Client[];
  isLoading: boolean;
  selectClient: (clientId: string) => void;
  setCurrentClientInfo: (clientId: string, clientName: string) => void;
}

const EditorClientContext = createContext<EditorClientContextValue | undefined>(undefined);

interface EditorClientProviderProps {
  children: ReactNode;
}

export const EditorClientProvider: React.FC<EditorClientProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [currentClientId, setCurrentClientId] = useState<string | null>(null);
  const [currentClientName, setCurrentClientName] = useState<string | null>(null);
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setIsLoading(true);
    try {
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/editor/clients`, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setClients(data.clients || []);
      }
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const selectClient = (clientId: string) => {
    navigate(`/editor/${clientId}/library/images`);
  };

  const setCurrentClientInfo = (clientId: string, clientName: string) => {
    setCurrentClientId(clientId);
    setCurrentClientName(clientName);
  };

  return (
    <EditorClientContext.Provider
      value={{
        currentClientId,
        currentClientName,
        clients,
        isLoading,
        selectClient,
        setCurrentClientInfo,
      }}
    >
      {children}
    </EditorClientContext.Provider>
  );
};

export const useEditorClient = () => {
  const context = useContext(EditorClientContext);
  if (context === undefined) {
    throw new Error('useEditorClient must be used within an EditorClientProvider');
  }
  return context;
};
